import { useRef, type RefObject } from 'react';
import * as React from 'react';
import {
  useListBox,
  useOption,
  type AriaOptionProps,
  type AriaListBoxOptions,
} from 'react-aria';
import { type SelectState, type Node } from 'react-stately';
import styles from './SelectListBox.module.css';

interface ListBoxProps<T extends object> extends AriaListBoxOptions<T> {
  testid?: string;
  state: SelectState<T>;
  listBoxRef?: RefObject<HTMLUListElement>;
}

interface OptionProps<T extends object> extends AriaOptionProps {
  testid?: string;
  state: SelectState<T>;
  item: Node<T>;
}

export function SelectListBox<T extends object>(props: ListBoxProps<T>) {
  const internalRef = useRef<HTMLUListElement>(null);
  const ref = props.listBoxRef ?? internalRef;
  const { testid, state } = props;
  const { listBoxProps } = useListBox(props, state, ref);

  return (
    <ul {...listBoxProps} ref={ref}>
      {Array.from(state.collection).map((item, index) => (
        <SelectListBox.Option
          key={item.key}
          item={item}
          state={state}
          testid={`${testid}-${index}`}
        />
      ))}
    </ul>
  );
}

function SelectListBoxOption<T extends object>(props: OptionProps<T>) {
  const { testid, item, state } = props;
  const ref = React.useRef(null);
  const { optionProps, isFocused, isSelected } = useOption(
    { key: item.key },
    state,
    ref
  );

  return (
    <li
      {...optionProps}
      data-testid={testid}
      ref={ref}
      className={`${styles.item} ${
        isSelected ? styles['is-active'] : undefined
      } ${isFocused ? styles['is-focused'] : undefined}`}
    >
      {item.rendered}
    </li>
  );
}

SelectListBox.Option = SelectListBoxOption;
